<template>
  <ComplianceReviewModalShell
    :show="show"
    :title="modalTitle"
    :loading="loading"
    :applicable-compliances="applicableCompliances"
    :empty-message="$t('No applicable compliances found for this staffing agency.')"
    cancel-data-test="recruiter-compliance-review-cancel"
    @close="$emit('close')"
  >
    <template #footer>
      <RecruiterComplianceReviewDecision
        v-if="complianceReviewEnabled && recruiterId && user.hasPermission('manage-compliance-tracker')"
        :recruiter-id="recruiterId"
        :loading="loading"
        :initial-decision="priorDecision"
        :initial-note="priorNote"
        @success="handleSaved"
        @cancel="$emit('close')"
      />
      <WdsButton
        v-else
        tertiary
        data-test="recruiter-compliance-review-cancel"
        @click="$emit('close')"
      >
        {{ $t('Cancel') }}
      </WdsButton>
    </template>
  </ComplianceReviewModalShell>
</template>

<script lang="ts" setup>
import { computed } from 'vue'
import { useQuery } from '@vue/apollo-composable'
import { WdsButton } from '@worksome/wds-components'
import { graphql } from '@/gql'
import {
  complianceMessage,
  complianceStatus,
} from '@/components/GraphqlDataTable/columns/Compliance/verifiedComplianceData'
import { ComplianceName, RecruiterReviewDecision } from '@/gql/graphql'
import { $t } from '@/app-bootstrapping/SetUpInternationalization'
import ComplianceReviewModalShell from '@/components/compliances/ComplianceReviewModalShell.vue'
import RecruiterComplianceReviewDecision from '@/components/compliances/ComplianceReviewModal/RecruiterComplianceReviewDecision.vue'
import { useAccountSettings } from '@/composables/useAccountSettings'
import { user } from '@/composables/useUser'

const props = defineProps<{
  show: boolean
  companyRecruiterId: string
}>()

const emit = defineEmits<{ close: []; saved: [] }>()

function handleSaved() {
  emit('saved')
  emit('close')
}

const RecruiterComplianceReviewModalQuery = graphql(`
  query RecruiterComplianceReviewModal($id: ID!) {
    companyRecruiter(id: $id) {
      id
      recruiter {
        id
        name
      }
      compliances {
        name
        applicable
        data {
          ...VerifiedComplianceStatus
          ...VerifiedComplianceMessage
        }
        ...ComplianceSection
      }
    }
  }
`)

const { result, loading } = useQuery(
  RecruiterComplianceReviewModalQuery,
  () => ({ id: props.companyRecruiterId }),
  { clientId: 'publicApi' },
)

const recruiterId = computed(() => result.value?.companyRecruiter?.recruiter?.id ?? '')

const modalTitle = computed(() => {
  const name = result.value?.companyRecruiter?.recruiter?.name
  return name ? $t('Review :name', { name }) : $t('Review staffing agency')
})

const applicableCompliances = computed(() =>
  (result.value?.companyRecruiter?.compliances ?? []).filter((c) => c.applicable),
)

const verifiedComplianceData = computed(
  () =>
    (result.value?.companyRecruiter?.compliances ?? []).find(
      (c) => c.name === ComplianceName.VerifiedRecruiterCompliance,
    )?.data,
)

const priorDecision = computed<RecruiterReviewDecision | null>(() => {
  const status = complianceStatus(verifiedComplianceData.value)
  if (status === 'CLEARED') return RecruiterReviewDecision.Cleared
  if (status === 'CHANGES_REQUESTED') return RecruiterReviewDecision.ChangesRequested
  return null
})

const priorNote = computed(() => complianceMessage(verifiedComplianceData.value, 'Message to staffing agency') ?? '')

const complianceReviewEnabled = useAccountSettings('compliance_tracker_enabled')
</script>
